import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Building2, FileText, AlertCircle, Loader2, Calendar } from 'lucide-react';
import SignaturePad from '../components/SignaturePad';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public subcontractor contract signing page. Accessible via one-time tokenised
 * link emailed to the trainer by the admin; no authentication required.
 */
export default function TrainerContractSignPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/trainer-contracts/${token}`)
      .then(({ data }) => {
        setData(data);
        if (data.contract?.signed_at) setSigned(true);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Lien invalide ou expiré'))
      .finally(() => setLoading(false));
  }, [token]);

  const onSign = async (dataUrl, name) => {
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/public/trainer-contracts/${token}/sign`, {
        signatory_name: name, signature_b64: dataUrl,
      });
      setSigned(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de la signature');
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAFE] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#4F46E5]" />
    </div>
  );

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFE] flex items-center justify-center p-6">
        <div className="soft-card p-8 max-w-md text-center" data-testid="contract-sign-error">
          <AlertCircle className="w-12 h-12 text-[#EF4444] mx-auto mb-3" />
          <h1 className="text-[18px] font-bold text-[#1A1B2E] mb-1">Lien non valide</h1>
          <p className="text-[13px] text-[#6B6C85]">{error}</p>
        </div>
      </div>
    );
  }

  const c = data.contract || {};
  const t = data.trainer || {};

  return (
    <div className="min-h-screen bg-[#FAFAFE] py-10 px-4" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="contract-sign-page">
      <div className="max-w-2xl mx-auto">
        <header className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-[11.5px] uppercase tracking-wider text-[#8E8FA5] font-semibold">Signature électronique</div>
            <h1 className="text-[20px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Contrat de sous-traitance {c.id}
            </h1>
          </div>
        </header>

        <section className="soft-card p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#4F46E5]" />
              <span className="text-[13.5px] font-semibold text-[#1A1B2E]">{t.name}</span>
            </div>
            {c.public_sign_expires_at && (
              <div className="text-[11.5px] text-[#8E8FA5]">Lien valide jusqu'au {new Date(c.public_sign_expires_at).toLocaleDateString('fr-FR')}</div>
            )}
          </div>

          <div className="space-y-2 text-[13px] text-[#4A4B63]">
            {t.speciality && <div><strong className="text-[#1A1B2E]">Spécialité :</strong> {t.speciality}</div>}
            <div><strong className="text-[#1A1B2E]">Objet :</strong> {c.mission_description || '—'}</div>
            {(c.start_date || c.end_date) && (
              <div className="inline-flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-[#4F46E5]" />
                <span>Du {c.start_date || '—'} au {c.end_date || '—'}</span>
              </div>
            )}
            {c.daily_rate != null && (
              <div><strong className="text-[#1A1B2E]">Taux journalier :</strong> {Number(c.daily_rate).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € HT</div>
            )}
            {c.notes && <div className="text-[#6B6C85] italic">Note : {c.notes}</div>}
          </div>

          <p className="text-[11.5px] text-[#8E8FA5] mt-4 pt-3 border-t border-[#F3F4F8]">
            Le PDF complet du contrat vous a été envoyé par email. En signant ci-dessous, vous acceptez les termes du contrat de sous-traitance.
          </p>
        </section>

        {signed ? (
          <div className="soft-card p-8 text-center" data-testid="contract-signed-ok">
            <CheckCircle2 className="w-14 h-14 text-[#14B8A6] mx-auto mb-3" />
            <h2 className="text-[19px] font-bold text-[#1A1B2E] mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Contrat signé avec succès
            </h2>
            <p className="text-[13px] text-[#6B6C85]">
              Merci ! Votre contrat a été accepté par {c.signatory_name || 'vous'}. Un PDF contresigné vous sera transmis sous peu.
            </p>
          </div>
        ) : (
          <section className="soft-card p-6">
            <h2 className="text-[15px] font-semibold text-[#1A1B2E] mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Accepter et signer le contrat
            </h2>
            <p className="text-[12px] text-[#6B6C85] mb-4">Apposez votre signature pour valider ce contrat de sous-traitance.</p>
            <SignaturePad initialName={t.name || ''} onSave={onSign} onCancel={() => {}} />
            {submitting && <div className="mt-2 text-[12px] text-[#4F46E5] flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</div>}
          </section>
        )}

        <footer className="mt-6 text-center text-[11px] text-[#8E8FA5]">
          Signature hébergée par <strong>{data.org?.name || 'OFManager'}</strong> · Lien sécurisé unique
        </footer>
      </div>
    </div>
  );
}
